import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { updateAnalyticsConsent } from "@/lib/analytics";

/**
 * Lightweight GDPR/CCPA cookie consent banner.
 *
 * Behavior:
 * - Essential cookies (auth session) are always set — no consent needed for
 *   those under GDPR "strictly necessary" carve-out.
 * - Analytics (PostHog) start opted-out (see index.html PostHog init) and
 *   are only enabled after explicit "Accept all" click here.
 * - Choice is persisted in localStorage under a versioned key so we can
 *   force a re-prompt if we materially change what we track.
 * - Banner never shows again once a choice is made — until the user clears
 *   storage or we bump the version.
 */

const STORAGE_KEY = "colleague_cookie_consent_v1";

function applyChoice(choice) {
  try {
    if (choice === "accepted" && window.posthog?.opt_in_capturing) {
      window.posthog.opt_in_capturing();
    } else if (choice === "rejected" && window.posthog?.opt_out_capturing) {
      window.posthog.opt_out_capturing();
    }
  } catch {
    /* posthog may not be loaded — no-op */
  }
  // Flip GA4 Consent Mode in lockstep with PostHog.
  updateAnalyticsConsent(choice);
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* private mode — treat as no choice, but don't crash */
    }
    if (stored === "accepted" || stored === "rejected") {
      applyChoice(stored);
      return;
    }
    // Small delay so it doesn't compete with the disclaimer / welcome modals
    const t = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(t);
  }, []);

  const choose = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* ignore */
    }
    applyChoice(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-2xl"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
    >
      <div className="bg-[#2D2926] text-[#F5F1EA] rounded-2xl shadow-2xl border border-[#3D3833] px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 text-sm leading-relaxed">
            <p className="text-[#F5F1EA]">
              We use a small set of cookies to keep you signed in and, if you
              agree, to understand how the site is used so we can make it
              better. Nothing is sold, ever.
            </p>
            <p className="mt-1.5 text-[13px] text-[#B7AF9F]">
              Details in our{" "}
              <Link
                to="/privacy"
                data-testid="cookie-banner-privacy-link"
                className="underline underline-offset-4 hover:text-[#F5F1EA]"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              type="button"
              data-testid="cookie-banner-reject"
              onClick={() => choose("rejected")}
              className="text-sm px-4 py-2 rounded-full border border-[#3D3833] text-[#F5F1EA] hover:bg-[#3D3833] transition-colors"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              data-testid="cookie-banner-accept"
              onClick={() => choose("accepted")}
              className="text-sm px-4 py-2 rounded-full bg-[#D4A574] text-[#2D2926] hover:bg-[#C99560] font-medium transition-colors"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
